import React, { useEffect, useRef, useState } from "react";
import { Collapse } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";

import CashCategoryServices from "../../../services/CashCategoryServices";
import CashbookServices from "../../../services/CashbookServices";
import CashEntryServices from "../../../services/CashEntryServices";
import CashOutEntryRow from "./CashOutEntryRow";

const readData = (response) => {
  const body = response?.data?.status ? response.data : response;
  return body?.data;
};

const getAssignedStores = () => {
  try {
    const user = JSON.parse(localStorage.getItem("userDetails"));
    const stores = user?.data?.user?.store;
    return Array.isArray(stores) ? stores : [];
  } catch {
    return [];
  }
};

const toInitCap = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

const formatAmount = (value) => {
  const amount = Number(value);

  return (Number.isFinite(amount) ? amount : 0).toLocaleString(
    "en-IN",
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }
  );
};

const getLocalDateTime = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");

  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}`
  );
};

const createRow = (id) => ({
  id,
  dateTime: getLocalDateTime(),
  categoryId: "",
  amount: "",
  paymentMode: "",
  vendorId: "",
  voucherNo: "",
  remark: "",
  attachments: []
});

const amountFields = [
  {
    key: "totalIn",
    label: "Total In",
    icon: "fa fa-arrow-down",
    className: "erp-cash-badge-in"
  },
  {
    key: "totalOut",
    label: "Total Out",
    icon: "fa fa-arrow-up",
    className: "erp-cash-badge-out"
  },
  {
    key: "balance",
    label: "Net Balance",
    icon: "fa fa-money",
    className: "erp-cash-badge-balance"
  }
];

const paymentModeOptions = [
  { value: "cash", label: "Cash" },
  { value: "online", label: "Online" }
];

const CashOutForm = () => {
  const [open, setOpen] = useState(true);
  const [stores] = useState(getAssignedStores);

  const [storeId, setStoreId] = useState(() =>
    String(
      stores.find((store) => store.status !== "inactive")?._id || ""
    )
  );

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState(() => [createRow(1)]);

  const [passbookState, setPassbookState] = useState({
    storeId: "",
    loading: false,
    passbook: null,
    error: false
  });

  const nextRowId = useRef(2);
  const submittingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      setLoading(true);

      try {
        const response =
          await CashCategoryServices.getCashCategories("active", "out");

        const data = readData(response);

        if (!Array.isArray(data?.categories)) {
          throw new Error("Unexpected category API response.");
        }

        if (!cancelled) setCategories(data.categories);
      } catch (error) {
        if (!cancelled) {
          Swal.fire(
            "Error",
            error?.response?.data?.message ||
            error?.message ||
            "Unable to load cash categories.",
            "error"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!storeId) {
      setPassbookState({
        storeId: "",
        loading: false,
        passbook: null,
        error: false
      });
      return;
    }

    setPassbookState({
      storeId,
      loading: true,
      passbook: null,
      error: false
    });

    const loadPassbook = async () => {
      try {
        const response =
          await CashbookServices.getMyPassbookByStore(storeId);

        const data = readData(response);

        if (
          !data ||
          !Object.prototype.hasOwnProperty.call(data, "passbook")
        ) {
          throw new Error("Unexpected passbook API response.");
        }

        if (!cancelled) {
          setPassbookState({
            storeId,
            loading: false,
            passbook: data.passbook,
            error: false
          });
        }
      } catch (error) {
        if (!cancelled) {
          setPassbookState({
            storeId,
            loading: false,
            passbook: null,
            error: true
          });

          Swal.fire(
            "Error",
            error?.response?.data?.message ||
            error?.message ||
            "Unable to load passbook.",
            "error"
          );
        }
      }
    };

    loadPassbook();

    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const storeOptions = stores.map((store) => ({
    value: String(store._id),
    label: [toInitCap(store.name), toInitCap(store.location)]
      .filter(Boolean)
      .join(", "),
    isDisabled: store.status === "inactive"
  }));

  const categoryOptions = categories.map((category) => ({
    value: String(category._id),
    label: category.name
  }));

  const currentPassbook =
    storeId &&
      passbookState.storeId === storeId &&
      !passbookState.loading &&
      !passbookState.error
      ? passbookState.passbook
      : null;

  const entryDisabled =
    !currentPassbook || currentPassbook.status !== "active";

  const formDisabled = entryDisabled || saving;

  let passbookTitle = "Select Store";

  if (storeId) {
    if (
      passbookState.storeId !== storeId ||
      passbookState.loading
    ) {
      passbookTitle = "Loading Passbook...";
    } else if (passbookState.error) {
      passbookTitle = "Unable to load passbook";
    } else if (currentPassbook) {
      passbookTitle = toInitCap(currentPassbook.name);
    } else {
      passbookTitle = "No active passbook found for this store";
    }
  }

  const updateRow = (id, field, value) => {
    if (formDisabled || submittingRef.current) return;

    setRows((previous) =>
      previous.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  const getRowValidationErrors = (row) => {
    const errors = [];

    if (
      !row.dateTime ||
      !Number.isFinite(new Date(row.dateTime).getTime())
    ) {
      errors.push("Date & Time");
    }

    if (
      !categoryOptions.some(
        (option) => option.value === row.categoryId
      )
    ) {
      errors.push("Category");
    }

    const amount = Number(row.amount);

    if (
      String(row.amount ?? "").trim() === "" ||
      !Number.isFinite(amount) ||
      amount <= 0
    ) {
      errors.push("Amount greater than 0");
    }

    if (
      !paymentModeOptions.some(
        (option) => option.value === row.paymentMode
      )
    ) {
      errors.push("Payment Mode");
    }

    if (!String(row.voucherNo || "").trim()) {
      errors.push("Voucher No / Transaction ID");
    }

    if (!String(row.remark || "").trim()) {
      errors.push("Remark");
    }

    if (!String(row.vendorId || "").trim()) {
      errors.push("Vendor Name");
    }

    return errors;
  };

  const invalidRowIndex = rows.findIndex(
    (row) => getRowValidationErrors(row).length > 0
  );

  const canSubmit =
    !formDisabled &&
    !loading &&
    rows.length > 0 &&
    rows.length <= 100 &&
    invalidRowIndex === -1;

  const canAddRow = canSubmit && rows.length < 100;

  const addRow = () => {
    if (
      formDisabled ||
      submittingRef.current ||
      loading ||
      rows.length >= 100
    ) {
      return;
    }

    if (invalidRowIndex !== -1) {
      Swal.fire(
        `Complete Row ${invalidRowIndex + 1}`,
        `Please enter: ${getRowValidationErrors(
          rows[invalidRowIndex]
        ).join(", ")}.`,
        "warning"
      );
      return;
    }

    const newRow = createRow(nextRowId.current++);
    setRows((previous) => [...previous, newRow]);
  };

  const removeRow = (id) => {
    if (formDisabled || submittingRef.current) return;

    setRows((previous) =>
      previous.length > 1
        ? previous.filter((row) => row.id !== id)
        : previous
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submittingRef.current) return;

    if (entryDisabled || loading || !currentPassbook?._id) {
      await Swal.fire(
        "Passbook Required",
        "Please select a store with an active passbook.",
        "warning"
      );
      return;
    }

    if (!rows.length || rows.length > 100) {
      await Swal.fire(
        "Entry Rows",
        "Please submit between 1 and 100 rows.",
        "warning"
      );
      return;
    }

    if (invalidRowIndex !== -1) {
      await Swal.fire(
        `Complete Row ${invalidRowIndex + 1}`,
        `Please enter: ${getRowValidationErrors(
          rows[invalidRowIndex]
        ).join(", ")}.`,
        "warning"
      );
      return;
    }

    // Image uploading will be implemented later.
    if (rows.some((row) => (row.attachments || []).length > 0)) {
      await Swal.fire(
        "Attachments Not Enabled",
        "Please remove the selected attachments before submitting. File uploading will be added later.",
        "info"
      );
      return;
    }

    submittingRef.current = true;
    setSaving(true);

    try {
      const payload = {
        passbookId: currentPassbook._id,
        entryType: "out",
        entries: rows.map((row) => ({
          dateTime: new Date(row.dateTime).toISOString(),
          categoryId: row.categoryId,
          amount: Number(row.amount),
          paymentMode: row.paymentMode,
          vendorId: row.vendorId,
          voucherNo: row.voucherNo.trim(),
          remark: row.remark.trim(),
          image: []
        }))
      };

      await CashEntryServices.createCashEntries(payload);
    } catch (error) {
      submittingRef.current = false;
      setSaving(false);

      await Swal.fire(
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to submit Cash Out entries.",
        "error"
      );
      return;
    }

    await Swal.fire({
      icon: "success",
      title: "Submitted",
      text: "Cash Out entries submitted for Site Manager approval.",
      confirmButtonText: "OK",
      allowOutsideClick: false,
      allowEscapeKey: false
    });

    setRows([createRow(nextRowId.current++)]);
    submittingRef.current = false;
    setSaving(false);
  };

  return (
    <div className="row erp-compact">
      <div className="col-xl-12">
        <div className="filter cm-content-box box-primary">
          <div className="content-title erp-cash-summary-header pt-3">
            <div className="cpa">
              <i className="fa fa-arrow-up me-2" aria-hidden="true" />
              Cash Out Entry
            </div>

            <div className="erp-cash-summary-middle">
              {amountFields.map((field) => (
                <div
                  key={field.key}
                  className={`erp-cash-badge ${field.className}`}
                >
                  <i className={field.icon} aria-hidden="true" />

                  <span>
                    <span className="erp-cash-badge-label">
                      {field.label}
                    </span>
                    <span className="erp-cash-badge-value">
                      {formatAmount(currentPassbook?.[field.key])}
                    </span>
                  </span>
                </div>
              ))}
            </div>

            <div className="tools erp-cash-tools">
              <Link
                to="#"
                className={`SlideToolHeader ${open ? "collapse" : "expand"
                  }`}
                onClick={(event) => {
                  event.preventDefault();
                  setOpen((previous) => !previous);
                }}
                aria-label="Toggle cash out form"
                aria-expanded={open}
              >
                <i className="fas fa-angle-up" aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="row justify-content-center px-3 pt-2 mb-2 pb-2">
            <div className="col-12 col-sm-8 col-md-5 col-lg-4 col-xl-3">
              <label htmlFor="cash-out-store" className="form-label">
                Store / Location
              </label>

              <Select
                inputId="cash-out-store"
                options={storeOptions}
                value={
                  storeOptions.find(
                    (option) => option.value === storeId
                  ) || null
                }
                onChange={(selected) => {
                  if (!submittingRef.current) {
                    setStoreId(selected?.value || "");
                  }
                }}
                placeholder="Select Store / Location"
                isSearchable
                isClearable
                isDisabled={saving}
                noOptionsMessage={() => "No assigned stores found"}
                className="erp-select"
                classNamePrefix="erp-select"
                menuPlacement="auto"
              />
            </div>
          </div>

          <Collapse in={open}>
            <div className="cm-content-body form excerpt">
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-2 mb-3">
                    <div className="form-group col-12">
                      <label
                        id="cash-out-passbook-label"
                        className="form-label"
                      >
                        Passbook Name
                      </label>

                      <div
                        className="form-control erp-readonly erp-passbook-name"
                        role="status"
                        aria-live="polite"
                        aria-labelledby="cash-out-passbook-label"
                        title={passbookTitle}
                      >
                        <i
                          className="fa fa-book me-2 text-primary"
                          aria-hidden="true"
                        />
                        <span>{passbookTitle}</span>
                      </div>
                    </div>
                  </div>

                  <fieldset disabled={formDisabled}>
                    {rows.map((row) => (
                      <CashOutEntryRow
                        key={row.id}
                        row={row}
                        disabled={formDisabled}
                        categoriesLoading={loading}
                        categoryOptions={categoryOptions}
                        paymentModeOptions={paymentModeOptions}
                        updateRow={updateRow}
                        onRemoveRow={() => removeRow(row.id)}
                        canRemoveRow={rows.length > 1}
                      />
                    ))}

                    <div className="d-flex flex-wrap gap-2 mt-2">
                      <button
                        type="button"
                        className="btn btn-primary erp-small-button"
                        onClick={addRow}
                        disabled={!canAddRow}
                      >
                        <i className="fa fa-plus me-2" aria-hidden="true" />
                        Add Row
                      </button>

                      <button
                        type="submit"
                        className="btn btn-success erp-small-button"
                        disabled={!canSubmit}
                      >
                        {saving ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              aria-hidden="true"
                            />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <i
                              className="fa fa-check me-2"
                              aria-hidden="true"
                            />
                            Submit
                          </>
                        )}
                      </button>
                    </div>

                    {!entryDisabled &&
                      !loading &&
                      invalidRowIndex !== -1 && (
                        <div
                          className="small text-muted mt-2"
                          role="status"
                          aria-live="polite"
                        >
                          Complete Row {invalidRowIndex + 1}:{" "}
                          {getRowValidationErrors(
                            rows[invalidRowIndex]
                          ).join(", ")}.
                        </div>
                      )}
                  </fieldset>
                </form>
              </div>
            </div>
          </Collapse>
        </div>
      </div>
    </div>
  );
};

export default CashOutForm;